import { DataListType } from '@models/module/fields/main';
import { ListResponse } from '@models/helpers';
import { GridSearchEntityInstance, SearchViewModel } from '@models/module/grid';
import { GridFieldsModel, Role } from '@models/module/main';
import { Permission } from '@models/permission';
import { Observable } from 'rxjs';
import {
  ActionEventType,
  ActionType,
  CipoTableOptions,
  EntityTableRow,
  FiltersToApply,
} from '@shared/components/data-list/common';
import { DropdownActionsEnum } from '@shared/consts/dropdown.consts';

export type DmActionType = ActionType<DropdownActionsEnum, EntityTableRow>;
export type DmTableOptions = CipoTableOptions<DmActionType, EntityTableRow> & { onlyPaged?: boolean };
export type DmActionEventType = ActionEventType<DropdownActionsEnum, EntityTableRow>;

/**
 * Permissions returned by the data-list stored procedure are scoped to the
 * individual document. Menu operations only describe the module capabilities
 * and must not be used to authorize actions on a specific row.
 */
export function hasRowPermission(
  row: Pick<GridSearchEntityInstance, 'permission_list'>,
  permission: Permission,
): boolean {
  return row?.permission_list?.split(',').some(permissionId => Number(permissionId.trim()) === permission) ?? false;
}

export function hasDeletePermission(row: Pick<GridSearchEntityInstance, 'permission_list'>): boolean {
  return hasRowPermission(row, Permission.Delete);
}

export type DmFiltersToApply = FiltersToApply & {
  isCurrent?: boolean;
  showPresets?: boolean;
};

export type HeaderFooterData = {
  topFormulas: Record<number, string>;
  bottomFormulas: Record<number, string>;
  bottomFormulaRows: { key: number; [key: string]: unknown }[];
};

export type DmQueryParams = {
  moduleId?: number;
  contractId?: number;
  workflowId?: number;
  entityInstanceId?: number;
  isCurrent?: boolean;
  relationProps?: RelationPropsType;
  dataListType?: DataListType;
};

export type DmTableContext = DmQueryParams & {
  customGridFieldsLoader?: (params: DmQueryParams) => Observable<GridFieldsModel>;
  version?: number;
};

export type DmTableDataRequest = DmQueryParams & {
  search: SearchViewModel;
  initialLoad?: boolean;
};

export type DmTableDataResponse = ListResponse<GridSearchEntityInstance> & {
  gridFields?: GridFieldsModel;
  search?: SearchViewModel;
};

export interface EntityInstanceTransition {
  id: number;
  name: string;
  //   should be an enum
  stateTypeId: string;
  roles: Role[];
  isDraft: boolean;
  isRevise: boolean;
  isComment: boolean;
  isUsedInMultiple: boolean;
  position: number;
  isEmail: boolean;
  isAutoTrigger: boolean;
  otherModule?: {
    currentFieldId: number;
    hasMultipleValues: boolean;
    moduleAbbr: string;
    moduleId: number;
    moduleName: string;
    perContract: boolean;
    screenId: number;
    actionId: number;
  };
}

export interface MultipleEntityInstanceTransition {
  entityInstanceId: number;
  transitions: EntityInstanceTransition[];
}

export type DmTransitionParams = {
  rows: EntityTableRow[];
  transition: EntityInstanceTransition;
  role?: Role;
  moduleId: number;
  contractId: number;
};

export enum DmUrlActionEnum {
  Create = '0',
  Clone = '1',
  Preset = '2',
}

export enum TransitionEnum {
  SaveDraft = 0,
}
export type ReloadDm = {
  reloadData?: boolean;
  entityInstanceIds?: number[];
};

export type RelationPropsType = {
  parentEntityInstanceId: number;
  relationId: number;
  fieldId?: number;
};

export type NotifyEmailsAndRolesModel = {
  emails: string[];
  groups: number[];
};

export interface WbsParentModel {
  wbsParentId: number;
  entityInstanceId: number;
  sourceProjectId: string;
  projectName: string;
  contextName?: string;
  contextIconId?: number;
  contextColor?: string;
  dataDate: string;
  createdOn: string;
}
